import { ActivityEndUser, ActivityEvent, ActivityType } from 'modules/accountActivity/domain'

const BUNDLED_SESSION_LENGTH_IN_MILLISECONDS = 1000 * 60 * 30 // 30 minutes
const DEFAULT_BUNDLE_SIZE = 20 // default size for page view bundles

// page view bundling
const shouldBundlePageViews = (firstActivity: ActivityEvent | undefined, secondActivity: ActivityEvent) => {
  if (!firstActivity) {
    return false
  }
  const usersMatch = firstActivity.endUserId === secondActivity?.endUserId
  const bothArePageView =
    firstActivity.activityType === ActivityType.PAGE_VIEW &&
    secondActivity.activityType === ActivityType.PAGE_VIEW

  const firstTimeStamp = firstActivity.createdAt
  const secondTimeStamp = secondActivity.createdAt
  const activitiesInSameSession =
    firstTimeStamp - secondTimeStamp < BUNDLED_SESSION_LENGTH_IN_MILLISECONDS

  return usersMatch && bothArePageView && activitiesInSameSession
}

export const pageViewBundle = (
  limit: number,
  activity: ActivityEvent,
  prevActivity: ActivityEvent | undefined,
  acc: ActivityEvent[]
) => {
  activity.activityContext.pageTitle = activity.activityContext.pageTitle || activity.activityContext.pageUrl;

  const newActivityContext = activity.activityContext;
  const newPageVisit = {
    url: newActivityContext.pageUrl,
    title: newActivityContext.pageTitle
  };

  const continuingBundle = shouldBundlePageViews(prevActivity, activity);

  const addNewBundle = () => {
    const bundledContext = {
      pageVisits: [{ url: newActivityContext.pageUrl, title: newActivityContext.pageTitle }],
      ...newActivityContext
    };
    acc.push({ ...activity, activityContext: bundledContext });
  };

  const addToExistingBundle = (activityBundle: ActivityEvent) => {
    const existingPageBundle = activityBundle.activityContext.pageVisits || [];
    if (newActivityContext.pageTitle && existingPageBundle.length < Math.min(DEFAULT_BUNDLE_SIZE, limit)) {
      existingPageBundle.push(newPageVisit);
    } else {
      addNewBundle();
      return;
    }

    const existingReferrer = activityBundle.activityContext.referrer;
    acc[acc.length - 1] = {
      ...activityBundle,
      activityContext: {
        ...activityBundle.activityContext,
        referrer: existingReferrer || newActivityContext.referrer,
        pageVisits: existingPageBundle
      }
    };
  };

  if (!continuingBundle) {
    addNewBundle();
  } else {
    const activityBundle = acc[acc.length - 1];
    addToExistingBundle(activityBundle);
  }

  return acc;
}

// meeting bundling
const shouldBundleMeetings = (firstActivity: ActivityEvent | undefined, secondActivity: ActivityEvent) => {
  if (!firstActivity) {
    return false
  }
  const meetingTypesMatch =
    firstActivity.activityContext.meetingType === secondActivity.activityContext.meetingType
  const meetingSlugsMatch =
    firstActivity.activityContext.slug === secondActivity.activityContext.slug
  const agentParticipantsMatch =
    firstActivity.activityContext.agentParticipants?.every(id =>
      secondActivity.activityContext.agentParticipants?.includes(id)
    ) &&
    secondActivity.activityContext.agentParticipants?.every(id =>
      firstActivity.activityContext.agentParticipants?.includes(id)
    )
  const endUserParticipantsMatch =
    firstActivity.activityContext.endUserParticipants?.every(id =>
      secondActivity.activityContext.endUserParticipants?.includes(id)
    ) &&
    secondActivity.activityContext.endUserParticipants?.every(id =>
      firstActivity.activityContext.endUserParticipants?.includes(id)
    )
  const startTimesMatch =
    firstActivity.activityContext.slotStart === secondActivity.activityContext.slotStart

  return (
    meetingTypesMatch &&
    meetingSlugsMatch &&
    agentParticipantsMatch &&
    endUserParticipantsMatch &&
    startTimesMatch
  )
}

export const meetingsBundle = (
  endUserData: ActivityEndUser,
  activity: ActivityEvent,
  prevActivty: ActivityEvent | undefined,
  acc: ActivityEvent[]
) => {
  const newActivityContext = activity.activityContext
  const endUserParticipant = endUserData
  const continuingBundle = shouldBundleMeetings(prevActivty, activity)

  if (!continuingBundle) {
    //start of new bundle
    const bundledContext = {
      ...newActivityContext,
      endUserParticipantObjects: [endUserParticipant]
    }
    acc.push({ ...activity, activityContext: bundledContext })
  } else {
    //add to existing bundle
    const activityBundle = acc[acc.length - 1]

    const bundledParticipants =
      activityBundle.activityContext.endUserParticipantObjects?.slice() ?? []
    bundledParticipants.push(endUserParticipant)

    const bundledContext = {
      ...newActivityContext,
      endUserParticipantObjects: bundledParticipants ?? []
    }

    acc[acc.length - 1] = {
      ...activityBundle,
      activityContext: bundledContext
    }
  }
  return acc
}

// email bundling
export const getEmailDataFromActivity = (activity: ActivityEvent) => {
  switch (activity.activityType) {
    case ActivityType.EMAIL_CLICK:
    case ActivityType.EMAIL_OPEN:
      return { id: activity.activityContext.id, subject: activity.activityContext.subject }
    case ActivityType.EMAIL_CLICK_SFDC:
    case ActivityType.EMAIL_OPEN_SFDC: {
      const subject = activity.activityContext.driftSfdcTask?.subject
      // SFDC open/click event subjects have the action prepended. ex. 'Opened Re: Some email subject'
      // so slice it out for users' readability
      return {
        id: activity.activityContext.driftSfdcTask?.taskId,
        subject: subject?.slice(subject.indexOf(' ') + 1),
        description: activity.activityContext.driftSfdcTask?.description
      }
    }
    case ActivityType.EMAIL_INBOUND:
      return {
        id: activity.activityContext.driftSfdcTask?.taskId,
        subject: activity.activityContext.driftSfdcTask?.subject,
        description: activity.activityContext.driftSfdcTask?.description
      }
  }
}

const checkEmailActivityTypeCompatibility = (first: ActivityType, second: ActivityType) => {
  switch (first) {
    case ActivityType.EMAIL_CLICK:
    case ActivityType.EMAIL_OPEN:
      return second === ActivityType.EMAIL_CLICK || second === ActivityType.EMAIL_OPEN
    case ActivityType.EMAIL_CLICK_SFDC:
    case ActivityType.EMAIL_OPEN_SFDC:
      return second === ActivityType.EMAIL_CLICK_SFDC || second === ActivityType.EMAIL_OPEN_SFDC
  }
}

const isEmailOpenEvent = (activity: ActivityEvent) =>
  activity.activityType === ActivityType.EMAIL_OPEN ||
  activity.activityType === ActivityType.EMAIL_OPEN_SFDC

const shouldBundleEmail = (firstActivity: ActivityEvent, secondActivity: ActivityEvent) => {
  const firstEmailData = getEmailDataFromActivity(firstActivity)
  const secondEmailData = getEmailDataFromActivity(secondActivity)

  if (!firstEmailData || !secondEmailData) {
    return false
  }

  const usersMatch = firstActivity.endUserId === secondActivity.endUserId
  const activityTypesAlign = checkEmailActivityTypeCompatibility(
    firstActivity.activityType,
    secondActivity.activityType
  )
  const emailIdsMatch = firstEmailData.id === secondEmailData.id
  const subjectsMatch = firstEmailData.subject === secondEmailData.subject

  if (
    secondActivity.activityType === ActivityType.EMAIL_OPEN_SFDC ||
    secondActivity.activityType === ActivityType.EMAIL_CLICK_SFDC
  ) {
    // SFDC event ids are tied to the event, not the email, so skip that check
    return activityTypesAlign && usersMatch && subjectsMatch
  }

  return activityTypesAlign && usersMatch && emailIdsMatch && subjectsMatch
}

export const emailBundle = (activity: ActivityEvent, acc: ActivityEvent[]) => {
  const isEmailOpen = isEmailOpenEvent(activity)

  // check if another event that matches the email is already in the timeline
  const existingEmailActivityIndex = acc.findIndex(existingActivity =>
    shouldBundleEmail(existingActivity, activity)
  )
  if (existingEmailActivityIndex < 0) {
    // if not found, simply add the event, with activity counters
    acc.push({
      ...activity,
      activityContext: {
        ...activity.activityContext,
        emailInteractionData: { opens: isEmailOpen ? 1 : 0, clicks: !isEmailOpen ? 1 : 0 },
        emailPreviewData: getEmailDataFromActivity(activity)
      }
    })
  } else {
    // if found, up the appropriate counter
    const bundledEvent = acc[existingEmailActivityIndex]
    if (bundledEvent.activityContext.emailInteractionData) {
      if (isEmailOpen) {
        bundledEvent.activityContext.emailInteractionData.opens++
      } else {
        bundledEvent.activityContext.emailInteractionData.clicks++
      }
    } else {
      bundledEvent.activityContext.emailInteractionData = {
        opens: isEmailOpen ? 1 : 0,
        clicks: !isEmailOpen ? 1 : 0
      }
    }
    bundledEvent.activityContext.emailPreviewData = getEmailDataFromActivity(activity)
    acc[existingEmailActivityIndex] = bundledEvent
  }
  return acc
}
